/**
 * Feedback API service
 * Submits user feedback (with optional screenshot) to the server,
 * which creates a GitHub issue on the project repository.
 */

const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

/**
 * Submit feedback to the server.
 *
 * @param {Object} feedbackData
 * @param {string} feedbackData.category      - 'bug' | 'feature' | 'feedback'
 * @param {string} feedbackData.description   - Min 10 characters
 * @param {string} [feedbackData.pageFeature] - Page/feature this relates to
 * @param {string} [feedbackData.email]       - User's email for follow-up
 * @param {boolean} [feedbackData.contactRequested]
 * @param {File}   [feedbackData.screenshot]  - Optional screenshot file
 * @param {string} [feedbackData.userId]      - Firebase UID if logged in
 * @param {boolean} [feedbackData.isLoggedIn]
 * @param {string} feedbackData.captchaToken  - reCAPTCHA v2 token
 * @returns {Promise<Object>}
 */
export async function submitFeedback(feedbackData) {
    const formData = new FormData();

    formData.append('category', feedbackData.category);
    formData.append('description', feedbackData.description);

    if (feedbackData.pageFeature) {
        formData.append('pageFeature', feedbackData.pageFeature);
    }

    if (feedbackData.email) {
        formData.append('email', feedbackData.email);
    }

    formData.append('contactRequested', feedbackData.contactRequested || false);
    formData.append('isLoggedIn', feedbackData.isLoggedIn || false);

    if (feedbackData.userId) {
        formData.append('userId', feedbackData.userId);
    }

    // Auto-captured metadata
    formData.append('submittedFrom', window.location.href);
    formData.append('userAgent', navigator.userAgent);
    formData.append('captchaToken', feedbackData.captchaToken);

    if (feedbackData.screenshot) {
        formData.append('screenshot', feedbackData.screenshot);
    }

    const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        body: formData,
        // No Content-Type header: browser auto-sets multipart boundary
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(
            error.error || `HTTP error! status: ${response.status}`
        );
    }

    return await response.json();
}

export default { submitFeedback };
